import { GetRegistrationModel } from "@factory/model/query-model-registration";

export const calendarUtils = {
  /**
   * Extracts the local date (YYYY-MM-DD) from a date string.
   * Handles both ISO strings (e.g., "2026-01-22T16:00:00.000Z") and plain dates ("2026-01-23").
   * Uses local timezone to avoid date shifting issues.
   */
  extractLocalDate: (date: string): string => {
    if (!date) return "";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "";
      // Use local date components to avoid UTC conversion issues
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  },

  /**
   * Normalizes time string to HH:mm:ss format
   * Handles various input formats: HH:mm, HH:mm:ss, or other variations
   */
  normalizeTime: (time: string): string => {
    if (!time) return "";
    // Remove any extra colons or invalid characters
    const cleanTime = time.trim();
    // If time already has seconds (HH:mm:ss), return as is
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(cleanTime)) {
      return cleanTime;
    }
    // If time is HH:mm format, add seconds
    if (/^\d{1,2}:\d{2}$/.test(cleanTime)) {
      return `${cleanTime}:00`;
    }
    // Try to extract valid time from the string
    const timeMatch = cleanTime.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      const hours = timeMatch[1].padStart(2, "0");
      const minutes = timeMatch[2];
      const seconds = timeMatch[3] || "00";
      return `${hours}:${minutes}:${seconds}`;
    }
    // Return empty if invalid format
    return "";
  },

  /**
   * Combines date and time into a datetime string for FullCalendar.
   * If time is not provided, returns just the date for all-day events.
   */
  combineDateTime: (date: string, time?: string): string => {
    if (!date) return "";
    try {
      const dateOnly = calendarUtils.extractLocalDate(date);
      if (!dateOnly) return "";

      // If no time provided, return date only (for all-day events)
      if (!time) {
        return dateOnly;
      }

      const normalizedTime = calendarUtils.normalizeTime(time);
      if (!normalizedTime) return dateOnly; // Fall back to date-only if time is invalid

      // Return local datetime string (FullCalendar handles this correctly)
      return `${dateOnly}T${normalizedTime}`;
    } catch {
      return "";
    }
  },

  /**
   * Adds one hour to the given date and time for event end time.
   * If time is not provided, returns empty (all-day events don't need end time).
   */
  addHourToTime: (date: string, time?: string): string => {
    if (!date) return "";
    try {
      const dateOnly = calendarUtils.extractLocalDate(date);
      if (!dateOnly) return "";

      // If no time, return empty (all-day event)
      if (!time) {
        return "";
      }

      const normalizedTime = calendarUtils.normalizeTime(time);
      if (!normalizedTime) return "";

      const dateTime = new Date(`${dateOnly}T${normalizedTime}`);
      if (isNaN(dateTime.getTime())) return "";
      dateTime.setHours(dateTime.getHours() + 1);

      // Return local datetime string
      const endYear = dateTime.getFullYear();
      const endMonth = String(dateTime.getMonth() + 1).padStart(2, "0");
      const endDay = String(dateTime.getDate()).padStart(2, "0");
      const endHours = String(dateTime.getHours()).padStart(2, "0");
      const endMinutes = String(dateTime.getMinutes()).padStart(2, "0");
      const endSeconds = String(dateTime.getSeconds()).padStart(2, "0");
      return `${endYear}-${endMonth}-${endDay}T${endHours}:${endMinutes}:${endSeconds}`;
    } catch {
      return "";
    }
  },

  formatClientName: (registration: GetRegistrationModel): string => {
    const parts = [
      registration.first_name,
      registration.middle_name,
      registration.last_name,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Unnamed Client";
  },

  formatEventTitle: (registration: GetRegistrationModel): string => {
    const clientName = calendarUtils.formatClientName(registration);
    const serviceName = registration.service_name || "Service";
    return `${clientName} - ${serviceName}`;
  },

  getStatusColor: (status: string): string => {
    const statusColors = {
      PENDING: "bg-blue-500",
      APPROVE: "bg-green-500",
      DECLINE: "bg-red-500",
    };
    return statusColors[status as keyof typeof statusColors] || "bg-gray-500";
  },

  getStatusBadgeClass: (status: string): string => {
    const statusClasses = {
      PENDING: "bg-blue-100 text-blue-800",
      APPROVE: "bg-green-100 text-green-800",
      DECLINE: "bg-red-100 text-red-800",
    };
    return (
      statusClasses[status as keyof typeof statusClasses] ||
      "bg-gray-100 text-gray-800"
    );
  },
};
